import { PropTypes, React } from 'imports';
import { addEvent, assertNever, isOutEvent, isOverEvent, removeEvent } from 'utils';
import Enum from 'utils/Enum';
import Popup, { ArrowSide } from '../Popup/Popup';
import ScrollBox from '../ScrollBox/ScrollBox';
import './Tooltip.less';

export const Direction = Enum('TOP', 'RIGHT', 'LEFT', 'BOTTOM');

export const TriggerType = Enum('HOVER', 'FOCUS');

export default class Tooltip extends React.Component {
    static get Direction() {
        return Direction;
    }

    static get TriggerType() {
        return TriggerType;
    }

    static get propTypes() {
        return {
            className: PropTypes.string,
            tagName: PropTypes.string,
            direction: PropTypes.oneOf(Object.values(Direction)),
            showTimeout: PropTypes.number,
            hideTimeout: PropTypes.number,
            disabled: PropTypes.bool,
            triggerType: PropTypes.oneOfType([
                PropTypes.oneOf(Object.values(Tooltip.TriggerType)),
                PropTypes.arrayOf(PropTypes.oneOf(Object.values(Tooltip.TriggerType)))
            ]),
            // disabled buttons don't raise mouseover/mouseout events and don't let mousemove to bubble through, so we have to add another node to catch the events
            // currently this hack does the opposite - catches all events and don't let them reach the button, so don't turn it on for enabled ones
            // TODO detect disabled parents automatically
            disabledButtonsHack: PropTypes.bool
        };
    }

    static get defaultProps() {
        return {
            className: '',
            tagName: 'div',
            direction: Direction.TOP,
            showTimeout: 500,
            hideTimeout: 200,
            disabled: false,
            triggerType: Tooltip.TriggerType.HOVER
        };
    }

    state = {
        visible: false,
        left: 0,
        top: 0,
        arrowOffset: undefined
    };

    _mouseOverTimeoutID = null;

    _mouseOutTimeoutID = null;

    _focusTimeoutID = null;

    _blurTimeoutID = null;

    get _arrowSide() {
        switch (this.props.direction) {
            case Direction.TOP:
                return ArrowSide.BOTTOM;
            case Direction.BOTTOM:
                return ArrowSide.TOP;
            case Direction.LEFT:
                return ArrowSide.RIGHT;
            case Direction.RIGHT:
                return ArrowSide.LEFT;
            default:
                return assertNever(this.props.direction, `Unknown direction ${this.props.direction}`);
        }
    }

    componentDidMount() {
        if (this._hasTriggerType(Tooltip.TriggerType.HOVER)) {
            if (!this.props.disabledButtonsHack) {
                addEvent(this.refs.root.refs.root.parentNode, 'mouseover', this._onParentMouseOver);
                addEvent(this.refs.root.refs.root.parentNode, 'mouseout', this._onParentMouseOut);
            }
            else {
                addEvent(this.refs.root.refs.root, 'mouseover', this._onParentMouseOver);
                addEvent(this.refs.root.refs.root, 'mouseout', this._onParentMouseOut);
                this.refs.root.refs.root.parentNode.className += ' disabled-buttons-hack';
            }
        }
        if (this._hasTriggerType(Tooltip.TriggerType.FOCUS)) {
            addEvent(this.refs.root.refs.root.parentNode, 'focusin', this._onParentFocus);
            addEvent(this.refs.root.refs.root.parentNode, 'focusout', this._onParentBlur);
        }

    }

    componentWillReceiveProps(nextProps) {
        if (this.props.disabledButtonsHack !== nextProps.disabledButtonsHack) {
            throw new Error('Can\'t change disabledButtonsHack of mounted component.');
        }
        if (!this.props.disabled && nextProps.disabled) {
            this._onClose();
        }
    }

    componentDidUpdate(_prevProps, prevState) {
        if (!prevState.visible && this.state.visible) {
            this._onOpen();
        }
    }

    componentWillUnmount() {
        this._clearTimeouts();
        if (this._hasTriggerType(Tooltip.TriggerType.HOVER)) {
            if (!this.props.disabledButtonsHack) {
                removeEvent(this.refs.root.refs.root.parentNode, 'mouseover', this._onParentMouseOver);
                removeEvent(this.refs.root.refs.root.parentNode, 'mouseout', this._onParentMouseOut);
            }
            else {
                removeEvent(this.refs.root.refs.root, 'mouseover', this._onParentMouseOver);
                removeEvent(this.refs.root.refs.root, 'mouseout', this._onParentMouseOut);
                this.refs.root.refs.root.parentNode.className = this.refs.root.refs.root.parentNode.className.replace(/\s*disabled-buttons-hack/g, '');
            }
        }
        if (this._hasTriggerType(Tooltip.TriggerType.FOCUS)) {
            removeEvent(this.refs.root.refs.root.parentNode, 'focusin', this._onParentFocus);
            removeEvent(this.refs.root.refs.root.parentNode, 'focusout', this._onParentBlur);
        }
    }

    _hasTriggerType = triggerType => Array.isArray(this.props.triggerType) ? this.props.triggerType.includes(triggerType) : this.props.triggerType === triggerType;

    _clearTimeouts() {
        if (this._mouseOverTimeoutID) {
            clearTimeout(this._mouseOverTimeoutID);
            this._mouseOverTimeoutID = null;
        }
        if (this._mouseOutTimeoutID) {
            clearTimeout(this._mouseOutTimeoutID);
            this._mouseOutTimeoutID = null;
        }
        if (this._blurTimeoutID) {
            clearTimeout(this._blurTimeoutID);
            this._blurTimeoutID = null;
        }
        if (this._focusTimeoutID) {
            clearTimeout(this._focusTimeoutID);
            this._focusTimeoutID = null;
        }
    }

    _onOpen = () => {
        this._clearTimeouts();

        const
            targetElementBounds = this.refs.root.refs.root.parentNode.getBoundingClientRect(),
            scrollingElement = document.scrollingElement || document.documentElement;

        switch (this.props.direction) {
            case Direction.TOP:
            case Direction.BOTTOM:
                const popupBounds = this.refs.popup?.container.firstElementChild.getBoundingClientRect() || null;

                if (!popupBounds) {
                    this.setState({
                        visible: true,
                        left: targetElementBounds.left + targetElementBounds.width / 2,
                        top: (this.props.direction === Direction.TOP ? targetElementBounds.top : targetElementBounds.bottom) + scrollingElement.scrollTop
                    });
                }
                else {
                    const
                        sideOffset = (popupBounds.width - targetElementBounds.width) / 2,
                        elementMidPoint = targetElementBounds.left + scrollingElement.scrollLeft + targetElementBounds.width / 2,
                        offset = sideOffset + targetElementBounds.width / 2;

                    this.setState({
                        visible: true,
                        left: elementMidPoint,
                        top: (this.props.direction === Direction.TOP ? targetElementBounds.top : targetElementBounds.bottom) + scrollingElement.scrollTop,
                        arrowOffset: elementMidPoint / 2 - offset > 0 ? offset : elementMidPoint - targetElementBounds.left - scrollingElement.scrollLeft
                    });
                }

                break;

            case Direction.LEFT:
            case Direction.RIGHT:
                this.setState({
                    visible: true,
                    left: this.props.direction === Direction.LEFT ? targetElementBounds.left : targetElementBounds.right,
                    top: targetElementBounds.top + targetElementBounds.height / 2 + scrollingElement.scrollTop
                });
                break;

            default:
                break;
        }
    };

    _onParentMouseOver = (nativeEvent) => {
        this._onMouseOver(this, nativeEvent);
    };

    _onParentFocus = (_nativeEvent) => {
        this._onFocus();
    };

    _onMouseOver = (sender, event) => {
        if (this.props.disabled || this._mouseOverTimeoutID || sender === this && !isOverEvent(event, !this.props.disabledButtonsHack ? this.refs.root.refs.root.parentNode : this.refs.root.refs.root)) {
            return;
        }

        if (this.state.visible) {
            this._clearTimeouts();
        }
        else {
            this._mouseOverTimeoutID = setTimeout(this._onOpen, this.props.showTimeout);
        }
    };

    _onFocus = () => {
        if (this.props.disabled || this._focusTimeoutID) {
            return;
        }

        if (this.state.visible) {
            this._clearTimeouts();
        }
        else {
            this._focusTimeoutID = setTimeout(this._onOpen, this.props.showTimeout);
        }
    };

    _onClose = () => {
        this._clearTimeouts();

        this.setState({ visible: false });
    };

    _onParentMouseOut = (nativeEvent) => {
        this._onMouseOut(this, nativeEvent);
    };

    _onParentBlur = (_nativeEvent) => {
        this._onBlur();
    };

    _onMouseOut = (sender, event) => {
        if (this.props.disabled || this._mouseOutTimeoutID || sender === this && !isOutEvent(event, !this.props.disabledButtonsHack ? this.refs.root.refs.root.parentNode : this.refs.root.refs.root)) {
            return;
        }

        if (!this.state.visible) {
            this._clearTimeouts();
        }
        else {
            this._mouseOutTimeoutID = setTimeout(this._onClose, this.props.hideTimeout);
        }
    };

    _onBlur = () => {
        if (this.props.disabled || this._blurTimeoutID) {
            return;
        }

        if (!this.state.visible) {
            this._clearTimeouts();
        }
        else {
            this._blurTimeoutID = setTimeout(this._onClose, this.props.hideTimeout);
        }
    };

    render() {
        const hoverEvents = this._hasTriggerType(Tooltip.TriggerType.HOVER) ?
            { onMouseOver: this._onMouseOver, onMouseOut: this._onMouseOut } :
            {};

        return (
            <ScrollBox ref="root" tagName={this.props.tagName} className="tooltip" global onScroll={this.state.visible ? this._onClose : null}>
                {
                    this.state.visible ?
                        <Popup {...this.state}
                            ref="popup"
                            fixed={false}
                            className={`tooltip ${this.props.className}`}
                            /* TODO split popup to to components with more understandable props */
                            /* one with arrow and direction/pointerX/pointerY */
                            /* another without arrow and left/right/top/bottom */
                            arrowSide={this._arrowSide}
                            onClose={this._onClose}
                            {...hoverEvents}>
                            {this.props.children}
                        </Popup> :
                        null
                }
            </ScrollBox>
        );
    }
}
